import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import ProtectedPageLayout from 'views/shared/ProtectedPageLayout';
import { NavigationPath } from 'types/DomainTypes';
import { useChatV2 } from './hooks/useChatV2';
import ChatPageLayout from './components/ChatPageLayout';
import WelcomeScreen from './components/WelcomeScreen';
import ChatMessageList from './components/ChatMessageList';
import ChatInputBar from './components/ChatInputBar';

const PortfolioChatPage: FC = () => {
  const { portalAiChat } = useFlags();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ldClient = useLDClient();
  const [loaded, setLoaded] = useState(false);

  const {
    messages,
    isStreaming,
    error,
    tier,
    contextLoading,
    insights,
    dismissInsight,
    sendMessage,
    clearHistory,
    clearError,
  } = useChatV2();

  const hasMessages = messages.filter((m) => m.role !== 'tool').length > 0;

  useEffect(() => {
    if (!ldClient) return;

    ldClient.waitForInitialization().then(() => {
      setLoaded(true);
    });
  }, [ldClient]);

  useEffect(() => {
    if (!portalAiChat && loaded) {
      navigate(NavigationPath.PORTFOLIO);
    }
  }, [portalAiChat, navigate, loaded]);

  if (!loaded || !portalAiChat) {
    return <div>Loading...</div>;
  }

  return (
    <ProtectedPageLayout
      view={NavigationPath.PORTFOLIO_CHAT}
      title={t('common:portfolioAssistant')}
      onBack={() => {}}
      containerClassName="overflow-hidden"
    >
      <ChatPageLayout tier={tier} onClearHistory={clearHistory}>
        {/* Error banner */}
        {error && (
          <div className="flex-shrink-0 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-3">
            <div className="max-w-3xl mx-auto flex items-center justify-between">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main content */}
        {hasMessages ? (
          <ChatMessageList messages={messages} />
        ) : (
          <WelcomeScreen
            tier={tier}
            contextLoading={contextLoading}
            insights={insights}
            onDismissInsight={dismissInsight}
            onSendMessage={sendMessage}
          />
        )}

        {/* Input bar */}
        <ChatInputBar onSendMessage={sendMessage} isStreaming={isStreaming} />
      </ChatPageLayout>
    </ProtectedPageLayout>
  );
};

export default PortfolioChatPage;
